import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useCreateSpecialMutation,
  useDeleteSpecialMutation,
  useUpdateSpecialMutation,
  useGetSpecialQuery,
} from "state/api";
import { Loader2 } from "lucide-react";

// Componentes separados
import NotificationToast from "./components/NotificationToast";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import FormHeader from "./components/FormHeader";
import SpecialFormFields from "./components/SpecialFormFields";
import ImagePreviewPanel from "./components/ImagePreviewPanel";

// Schema de validação
const specialSchema = Yup.object({
  name: Yup.string().required("O nome do especial é obrigatório."),
  slug: Yup.string().required("O slug é obrigatório."),
  description: Yup.string().optional(),
  weight: Yup.number().min(0, "O peso deve ser maior ou igual a 0").optional(),
  is_popular: Yup.boolean().optional(),
  image: Yup.string().url("URL da imagem inválida").optional(),
});

const defaultValue = {
  name: "",
  slug: "",
  description: "",
  weight: 0,
  is_popular: false,
  image: "",
};

const SpecialForm = ({ title, special = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Mutations e queries
  const [createSpecial] = useCreateSpecialMutation();
  const [updateSpecial] = useUpdateSpecialMutation();
  const [deleteSpecial] = useDeleteSpecialMutation();
  const { data: specialData, isLoading: isLoadingSpecial } = useGetSpecialQuery(id, {
    skip: !id
  });
  
  // Estados
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [notification, setNotification] = useState(null);
  const [imagePreview, setImagePreview] = useState({
    url: "",
    isValid: false,
    isLoading: false
  });

  // Formik setup
  const formik = useFormik({
    initialValues: special?.data || specialData?.data || defaultValue,
    validationSchema: specialSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const body = specialSchema.cast(values);
        let resp;
        
        if (id) {
          resp = await updateSpecial({ id, body });
        } else {
          resp = await createSpecial(body);
        }
        
        if (!resp.error) {
          setNotification({
            type: 'success',
            message: id ? 'Especial atualizado com sucesso!' : 'Especial criado com sucesso!'
          });
          
          if (!id) {
            setTimeout(() => navigate("/specials"), 1500);
          }
        } else {
          setNotification({
            type: 'error',
            message: resp.error?.data?.[0] || 'Erro ao salvar especial'
          });
        }
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Erro inesperado ao salvar especial'
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  // Auto-gerar slug baseado no nome
  useEffect(() => {
    if (formik.values.name && !id) {
      const slug = formik.values.name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      if (slug !== formik.values.slug) {
        formik.setFieldValue('slug', slug);
      }
    }
  }, [formik.values.name, id]);

  // Preview da imagem com validação
  useEffect(() => {
    const validateImage = async () => {
      if (!formik.values.image) {
        setImagePreview({ url: "", isValid: false, isLoading: false });
        return;
      }
      
      setImagePreview(prev => ({ ...prev, isLoading: true }));
      
      try {
        const img = new Image();
        img.onload = () => {
          setImagePreview({
            url: formik.values.image,
            isValid: true,
            isLoading: false
          });
        };
        img.onerror = () => {
          setImagePreview({
            url: formik.values.image,
            isValid: false,
            isLoading: false
          });
        };
        img.src = formik.values.image;
      } catch (error) {
        setImagePreview({
          url: formik.values.image,
          isValid: false,
          isLoading: false
        });
      }
    };

    const timeoutId = setTimeout(validateImage, 500);
    return () => clearTimeout(timeoutId);
  }, [formik.values.image]);

  // Handlers
  const handleDelete = useCallback(async () => {
    try {
      await deleteSpecial(id);
      setNotification({
        type: 'success',
        message: 'Especial excluído com sucesso!'
      });
      setTimeout(() => navigate("/special"), 1500);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Erro ao excluir especial'
      });
    }
    setShowDeleteDialog(false);
  }, [id, deleteSpecial, navigate]);

  const handleClone = useCallback(() => {
    const clonedValues = {
      ...formik.values,
      name: `${formik.values.name} (Cópia)`,
      slug: `${formik.values.slug}-copy`,
    };
    formik.setValues(clonedValues);
    setNotification({
      type: 'success',
      message: 'Especial clonado! Altere o nome e salve.'
    });
  }, [formik]);

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setNotification({
      type: 'success',
      message: 'Copiado para a área de transferência!'
    });
  }, []);

  // Loading state
  if (isLoadingSpecial) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Cabeçalho */}
        <FormHeader
          title={title}
          id={id}
          isValid={formik.isValid}
          isSubmitting={isSubmitting}
          special={special || specialData}
          onBack={() => navigate("/specials")}
          onClone={handleClone}
          onDelete={() => setShowDeleteDialog(true)}
        />

        {/* Formulário */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Coluna esquerda - Formulário */}
          <div className="space-y-6">
            <SpecialFormFields 
              formik={formik}
            />
          </div>

          {/* Coluna direita - Preview da Imagem */}
          <div className="space-y-6">
            <ImagePreviewPanel
              imagePreview={imagePreview}
              specialData={formik.values}
              onCopy={copyToClipboard}
            />
          </div>
        </div>
      </div>

      {/* Componentes de UI */}
      <NotificationToast 
        notification={notification} 
        onClose={() => setNotification(null)} 
      />
      <DeleteConfirmationModal
        isOpen={showDeleteDialog}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
        title="Excluir Especial"
        message="Tem certeza que deseja excluir este especial? Esta ação não pode ser desfeita."
      />
    </div>
  );
};

export default SpecialForm;